import { Controller } from "@hotwired/stimulus"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * Streak weeks and the reads sparkline.
 *
 * The line is drawn by measuring its own path length and animating
 * stroke-dashoffset — the one stroke property the global `body *` transition in
 * application.tailwind.css doesn't already claim.
 *
 * Counters read their target from `data-lp-to` and are written as text, so the
 * final value is in the markup and survives with JS off.
 */
export default class extends Controller {
  static targets = ["cell", "line", "count"]

  static values = { cellStagger: { type: Number, default: 58 } }

  connect() {
    if (window.matchMedia(REDUCED).matches) return

    this.timers = []
    this.frames = []

    this.lineTargets.forEach((line) => {
      const length = Math.ceil(line.getTotalLength ? line.getTotalLength() : 400)
      line.style.setProperty("--lp-len", length)
    })

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.play()
        } else {
          this.reset()
        }
      },
      { threshold: 0, rootMargin: "-20% 0px -20% 0px" }
    )

    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    this.clearAll()
  }

  play() {
    if (this.playing) return
    this.playing = true

    this.cellTargets.forEach((cell, i) => {
      this.after(i * this.cellStaggerValue, () => cell.classList.add("is-in"))
    })

    const afterCells = this.cellTargets.length * this.cellStaggerValue + 120
    this.lineTargets.forEach((line) => this.after(afterCells, () => line.classList.add("is-drawn")))
    this.countTargets.forEach((el) => this.after(afterCells, () => this.countUp(el)))
  }

  countUp(el) {
    const target = parseFloat(el.dataset.lpTo || el.textContent) || 0
    const suffix = el.dataset.lpSuffix || ""
    const duration = 1150
    const start = performance.now()

    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1)
      // Ease-out so the number settles instead of stopping dead.
      const eased = 1 - Math.pow(1 - p, 3)
      el.textContent = `${Math.round(target * eased).toLocaleString()}${suffix}`
      if (p < 1) this.frames.push(requestAnimationFrame(tick))
    }

    this.frames.push(requestAnimationFrame(tick))
  }

  reset() {
    this.clearAll()
    this.playing = false
    this.cellTargets.forEach((el) => el.classList.remove("is-in"))
    this.lineTargets.forEach((el) => el.classList.remove("is-drawn"))
    this.countTargets.forEach((el) => {
      const target = parseFloat(el.dataset.lpTo || 0) || 0
      el.textContent = `0${el.dataset.lpSuffix || ""}`
      if (!target) el.textContent = el.dataset.lpTo || "0"
    })
  }

  after(ms, fn) {
    this.timers.push(setTimeout(fn, ms))
  }

  clearAll() {
    ;(this.timers || []).forEach(clearTimeout)
    ;(this.frames || []).forEach(cancelAnimationFrame)
    this.timers = []
    this.frames = []
  }
}
